import { supabase } from '../supabase';
import { sendEmailWithFallback } from '../email';
import { sendSMS } from '../messaging';

/**
 * Independent (standalone) invoicing — thin client wrappers over the SECURITY
 * DEFINER RPCs added in 20260702000000_standalone_invoices.sql. These reuse the
 * existing invoices / invoice_line_items / recompute_invoice engine; the invoice
 * just isn't tied to a booking.
 */

export type InvoiceDiscountSource = { type: 'percentage' | 'fixed'; value: number } | null;

export interface InvoiceRow {
  id: string;
  business_id: string;
  booking_id: string | null;
  customer_id: string | null;
  invoice_number: string | null;
  status: 'draft' | 'open' | 'partially_paid' | 'paid' | 'void';
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  amount_due_cents: number;
  tax_breakdown: Array<{ name: string; rate_bps: number; amount_cents: number }>;
  discount_source: InvoiceDiscountSource;
  bill_to_name: string | null;
  bill_to_company: string | null;
  bill_to_email: string | null;
  bill_to_phone: string | null;
  bill_to_address: string | null;
  /** Customer PO reference. Null until staff type one. */
  po_number: string | null;
  notes: string | null;
  payment_terms: string | null;
  finalized_at: string | null;
  due_at: string | null;
  created_at: string;
}

export interface InvoiceLineRow {
  id: string;
  invoice_id: string;
  kind: string;
  description: string;
  quantity: number;
  unit_amount_cents: number;
  subtotal_cents: number;
  line_discount_cents: number;
  discount_cents: number;
  net_cents: number;
  taxable: boolean;
  tax_cents: number;
  total_cents: number;
  service_id: string | null;
  sort_order: number;
}

function ensure() {
  if (!supabase) throw new Error('Supabase not initialized');
  return supabase;
}

export async function createStandaloneInvoice(params: {
  businessId: string;
  customerId?: string | null;
  billToName?: string | null;
  billToEmail?: string | null;
  billToPhone?: string | null;
  billToAddress?: string | null;
  notes?: string | null;
}): Promise<string> {
  const { data, error } = await ensure().rpc('create_standalone_invoice', {
    p_business_id: params.businessId,
    p_customer_id: params.customerId ?? null,
    p_bill_to_name: params.billToName ?? null,
    p_bill_to_email: params.billToEmail ?? null,
    p_bill_to_phone: params.billToPhone ?? null,
    p_bill_to_address: params.billToAddress ?? null,
    p_notes: params.notes ?? null,
  });
  if (error) throw error;
  return data as string;
}

export async function updateStandaloneInvoice(params: {
  invoiceId: string;
  customerId?: string | null;
  billToName?: string | null;
  billToEmail?: string | null;
  billToPhone?: string | null;
  billToAddress?: string | null;
  notes?: string | null;
  discountSource?: InvoiceDiscountSource;
}): Promise<void> {
  const { error } = await ensure().rpc('update_standalone_invoice', {
    p_invoice_id: params.invoiceId,
    p_customer_id: params.customerId ?? null,
    p_bill_to_name: params.billToName ?? null,
    p_bill_to_email: params.billToEmail ?? null,
    p_bill_to_phone: params.billToPhone ?? null,
    p_bill_to_address: params.billToAddress ?? null,
    p_notes: params.notes ?? null,
    p_discount_source: params.discountSource ?? null,
  });
  if (error) throw error;
}

export async function addInvoiceLine(params: {
  invoiceId: string;
  description: string;
  quantity?: number;
  unitAmountCents: number;
  taxable?: boolean;
  lineDiscountCents?: number;
  serviceId?: string | null;
  sortOrder?: number;
}): Promise<string> {
  const { data, error } = await ensure().rpc('add_invoice_line', {
    p_invoice_id: params.invoiceId,
    p_description: params.description,
    p_quantity: params.quantity ?? 1,
    p_unit_amount_cents: params.unitAmountCents,
    p_taxable: params.taxable ?? true,
    p_line_discount_cents: params.lineDiscountCents ?? 0,
    p_service_id: params.serviceId ?? null,
    p_sort_order: params.sortOrder ?? 0,
  });
  if (error) throw error;
  return data as string;
}

export async function updateInvoiceLine(params: {
  lineId: string;
  description: string;
  quantity: number;
  unitAmountCents: number;
  taxable: boolean;
  lineDiscountCents: number;
}): Promise<void> {
  const { error } = await ensure().rpc('update_invoice_line', {
    p_line_id: params.lineId,
    p_description: params.description,
    p_quantity: params.quantity,
    p_unit_amount_cents: params.unitAmountCents,
    p_taxable: params.taxable,
    p_line_discount_cents: params.lineDiscountCents,
  });
  if (error) throw error;
}

export async function deleteInvoiceLine(lineId: string): Promise<void> {
  const { error } = await ensure().rpc('delete_invoice_line', { p_line_id: lineId });
  if (error) throw error;
}

export async function recomputeInvoice(invoiceId: string): Promise<void> {
  const { error } = await ensure().rpc('recompute_invoice_rpc', { p_invoice_id: invoiceId });
  if (error) throw error;
}

export async function issueInvoice(invoiceId: string, paymentTerms = 'due_on_receipt'): Promise<void> {
  const { error } = await ensure().rpc('issue_invoice', { p_invoice_id: invoiceId, p_payment_terms: paymentTerms });
  if (error) throw error;
}

export async function voidInvoice(invoiceId: string): Promise<void> {
  const { error } = await ensure().rpc('void_invoice', { p_invoice_id: invoiceId });
  if (error) throw error;
}

export async function deleteDraftInvoice(invoiceId: string): Promise<void> {
  const { error } = await ensure().rpc('delete_draft_invoice', { p_invoice_id: invoiceId });
  if (error) throw error;
}

/** Load an invoice header + its line items (for the editor / view). */
export async function getInvoiceWithLines(invoiceId: string): Promise<{ invoice: InvoiceRow; lines: InvoiceLineRow[] } | null> {
  const sb = ensure();
  const { data: invoice, error } = await sb.from('invoices').select('*').eq('id', invoiceId).maybeSingle();
  if (error) throw error;
  if (!invoice) return null;
  const { data: lines, error: linesErr } = await sb
    .from('invoice_line_items')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('sort_order', { ascending: true });
  if (linesErr) throw linesErr;
  return { invoice: invoice as InvoiceRow, lines: (lines || []) as InvoiceLineRow[] };
}

export interface InvoicePaymentRow {
  id: string;
  amount: number;
  base_amount: number | null;
  tip_amount: number | null;
  surcharge_amount: number | null;
  method: string | null;
  channel: string | null;
  status: string;
  payment_type: string | null;
  reference: string | null;
  notes: string | null;
  created_at: string;
  payment_token?: string | null;
}

/** Record a manual payment against a standalone invoice (reuses the payment engine). */
export async function recordInvoicePayment(params: {
  invoiceId: string;
  amount: number;
  method?: string;
  reference?: string | null;
  notes?: string | null;
  paymentType?: 'deposit' | 'final' | 'partial';
}): Promise<string> {
  const { data, error } = await ensure().rpc('record_invoice_payment', {
    p_invoice_id: params.invoiceId,
    p_amount: params.amount,
    p_method: params.method ?? 'cash',
    p_reference: params.reference ?? null,
    p_notes: params.notes ?? null,
    p_payment_type: params.paymentType ?? 'final',
  });
  if (error) throw error;
  return data as string;
}

export async function voidInvoicePayment(paymentId: string): Promise<void> {
  const { error } = await ensure().rpc('void_invoice_payment', { p_payment_id: paymentId });
  if (error) throw error;
}

/**
 * Override the bill-to snapshot on ANY invoice (booking-linked or standalone)
 * without touching the customer record. Works on draft and issued invoices.
 */
/** Set or clear the customer PO number. Blank clears it. Draft and issued invoices only. */
export async function updateInvoicePoNumber(params: {
  invoiceId: string;
  poNumber?: string | null;
}): Promise<void> {
  const { error } = await ensure().rpc('update_invoice_po_number', {
    p_invoice_id: params.invoiceId,
    p_po_number: params.poNumber ?? null,
  });
  if (error) throw error;
}

export async function updateInvoiceBillTo(params: {
  invoiceId: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
}): Promise<void> {
  const { error } = await ensure().rpc('update_invoice_bill_to', {
    p_invoice_id: params.invoiceId,
    p_name: params.name ?? null,
    p_email: params.email ?? null,
    p_phone: params.phone ?? null,
    p_address: params.address ?? null,
  });
  if (error) throw error;
}

/** Create a public Stripe pay link for an invoice's balance. Returns the URL. */
export async function createInvoicePaymentLink(invoiceId: string, amount: number): Promise<string> {
  const sb = ensure();
  const { data: paymentId, error } = await sb.rpc('create_invoice_payment_intent', { p_invoice_id: invoiceId, p_amount: amount });
  if (error) throw error;
  const { data: link, error: linkErr } = await sb.functions.invoke('generate-invoice-payment-link', { body: { paymentId } });
  if (linkErr) {
    const msg = (linkErr as any)?.context?.error || (linkErr as any)?.message || 'Failed to create payment link';
    throw new Error(msg);
  }
  if (!link?.paymentUrl) throw new Error(link?.error || 'Failed to create payment link');
  return link.paymentUrl as string;
}

/** Payments recorded against an invoice. */
export async function getInvoicePayments(invoiceId: string): Promise<InvoicePaymentRow[]> {
  const { data, error } = await ensure()
    .from('payments')
    .select('id, amount, base_amount, tip_amount, surcharge_amount, method, channel, status, payment_type, reference, notes, created_at, payment_token')
    .eq('invoice_id', invoiceId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as InvoicePaymentRow[];
}

/** Generate the PDF for a standalone invoice. Returns base64 + filename. */
export async function getInvoicePdf(invoiceId: string): Promise<{ base64: string; filename: string }> {
  const { data, error } = await ensure().functions.invoke('generate-invoice-pdf', { body: { invoiceId } });
  if (error) {
    const msg = (error as any)?.context?.error || (error as any)?.message || 'Failed to generate PDF';
    throw new Error(msg);
  }
  if (!data?.base64) throw new Error(data?.error || 'Failed to generate PDF');
  return { base64: data.base64 as string, filename: (data.filename as string) || 'invoice.pdf' };
}

async function logInvoiceSend(params: { invoiceId: string; businessId: string; via: 'email' | 'sms'; recipient: string; customerName?: string | null; payLink?: string | null }) {
  try {
    await ensure().from('invoice_history').insert({
      business_id: params.businessId,
      booking_id: null,
      invoice_id: params.invoiceId,
      sent_via: params.via,
      has_payment_link: !!params.payLink,
      payment_link_url: params.payLink || null,
      customer_email: params.via === 'email' ? params.recipient : null,
      customer_name: params.customerName || null,
    });
  } catch (e) {
    console.warn('invoice_history log failed (non-fatal):', e);
  }
}

/** Email a standalone invoice (PDF attached, optional pay link). */
export async function sendStandaloneInvoiceEmail(params: {
  invoiceId: string;
  businessId: string;
  businessName: string;
  to: string;
  subject: string;
  message: string;
  includePayLink?: boolean;
  payAmount?: number;
  customerName?: string | null;
}): Promise<void> {
  const pdf = await getInvoicePdf(params.invoiceId);
  let payLink: string | null = null;
  if (params.includePayLink && (params.payAmount ?? 0) > 0) {
    try { payLink = await createInvoicePaymentLink(params.invoiceId, params.payAmount!); } catch { /* link optional */ }
  }
  const messageHtml = (params.message || '').replace(/\n/g, '<br>');
  const payHtml = payLink
    ? `<div style="margin:24px 0;text-align:center;"><a href="${payLink}" style="background:#2563eb;color:#fff;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:600;">Pay invoice online</a></div>`
    : '';
  const body = `
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:600px;margin:0 auto;">
      <div style="padding:24px 32px;">
        <p style="color:#111827;font-size:15px;line-height:1.6;">${messageHtml}</p>
        ${payHtml}
      </div>
      <div style="padding:16px 32px;background:#f9fafb;border-top:1px solid #e5e7eb;">
        <p style="color:#9ca3af;font-size:13px;margin:0;text-align:center;">Invoice from ${params.businessName}</p>
      </div>
    </div>`;
  const res = await sendEmailWithFallback({
    businessId: params.businessId,
    to: params.to,
    subject: params.subject,
    body,
    attachments: [{ filename: pdf.filename, content: pdf.base64, type: 'application/pdf' }],
  });
  if (!res.success) throw new Error(res.error || 'Failed to send email');
  await logInvoiceSend({ invoiceId: params.invoiceId, businessId: params.businessId, via: 'email', recipient: params.to, customerName: params.customerName, payLink });
}

/** Text a standalone invoice (message + optional pay link). */
export async function sendStandaloneInvoiceSMS(params: {
  invoiceId: string;
  businessId: string;
  to: string;
  message: string;
  includePayLink?: boolean;
  payAmount?: number;
  customerName?: string | null;
}): Promise<void> {
  let payLink: string | null = null;
  if (params.includePayLink && (params.payAmount ?? 0) > 0) {
    try { payLink = await createInvoicePaymentLink(params.invoiceId, params.payAmount!); } catch { /* link optional */ }
  }
  const text = payLink ? `${params.message}\n\nPay online: ${payLink}` : params.message;
  const res = await sendSMS({ businessId: params.businessId, to: params.to, message: text });
  if (!res.success) throw new Error(res.error || 'Failed to send SMS');
  await logInvoiceSend({ invoiceId: params.invoiceId, businessId: params.businessId, via: 'sms', recipient: params.to, customerName: params.customerName, payLink });
}

/** Standalone (booking-less) invoices for the ledger list. */
export async function listStandaloneInvoices(businessId: string): Promise<InvoiceRow[]> {
  const { data, error } = await ensure()
    .from('invoices')
    .select('*')
    .eq('business_id', businessId)
    .is('booking_id', null)
    .is('proposal_id', null)
    // Voids included — the ledger's Void tab is the only consumer and it
    // filters by status per mode (voided order/counter-sale invoices must
    // stay visible there).
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as InvoiceRow[];
}
